import React from 'react'
import { Controller } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import SpecialInstruction from '../../../components/elements/SpecialInstruction'
import AsyncSelect from 'react-select/async'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import ViewSensitivePiiButtons from '../../../components/ViewSensitivePiiButtons'
import FormFieldBuilder from '../../../components/FormFieldBuilder'
import Loading from '../../../components/Loading'
import { useDmcModal } from './dmc/useDmcModal'
import { parseLabel, parseValue } from './dmc/dmcModalHelpers'
import DmcActionSummary from './dmc/DmcActionSummary'
import DmcDocumentSlot from './dmc/DmcDocumentSlot'

export default function DmcModal() {
  const dmc = useDmcModal()

  const renderDebtManagementCompanyForm = () => {
    if (dmc.moduleFields && dmc.module) {
      const filterAndSortByCustomFields = (moduleFields, customerFields) => {
        return moduleFields
          .filter((e) => customerFields.includes(e.column_name))
          .sort((a, b) => {
            return customerFields.indexOf(a.column_name) - customerFields.indexOf(b.column_name);
          });
      };

      const filteredFields = filterAndSortByCustomFields(dmc.moduleFields, dmc.fieldSets);

      const renderFields = (fields) => {
        return fields.map((m, index) => {
          const fieldType = dmc.dropdownOptions[m.module_field_type_dropdown_option_id]?.code_key;
          const selectOptions = {
            dropdownOptions: dmc.dropdowns?.data.find((e) => m.dropdown_key === e.code_key)?.dropdown_options,
            is_editable: true,
            fieldType,
          };

          const fieldName = m.column_name;

          let data = '';
          let sourceModule = {};
          if (!dmc.isCreating) {
            data = dmc.loanDmcData['loan_dmc'][fieldName] ?? "";

            const fieldNameLookup = {
              zip_code_id: dmc.loanDmcData?.loan_dmc?.zip_code?.label,
              dmc_id: dmc.loanDmcData?.loan_dmc?.dmc?.name
            }

            const defaultValue = fieldNameLookup[m.column_name] || data;
            sourceModule = {
              id: data,
              name: defaultValue,
              column_name: m.column_name,
            }
          }

          if (m.column_name === 'dmc_id') {
            const isRequired = dmc.watchDmcStatusId === dmc.dmcStatusConfirmedId
            return (
              <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column py-2" key="dmc-id-field">
                <p className="has-text-grey is-small is-size-8">
                  <span className="mr-1">{m.label_name}</span>
                  {isRequired && <span className="has-text-danger">*</span>}
                </p>
                <Controller
                  name="dmc_id"
                  control={dmc.control}
                  rules={{ required: isRequired }}
                  defaultValue={!dmc.isCreating ? dmc.loanDmcData?.loan_dmc?.dmc_id : null}
                  render={({ field: { onChange } }) => (
                    <AsyncSelect
                      cacheOptions={false}
                      isClearable
                      isSearchable
                      defaultOptions={false}
                      loadOptions={dmc.loadDmcOptions}
                      value={dmc.selectedDmcOption}
                      getOptionLabel={(e) => e.name}
                      getOptionValue={(e) => String(e.id)}
                      classNames={reactSelectClassNames(dmc.errors, 'dmc_id')}
                      noOptionsMessage={({ inputValue }) => (!inputValue ? 'Start typing to search...' : 'No results found')}
                      onChange={(val) => {
                        onChange(val?.id || null)
                        dmc.setSelectedDmcOption(val || null)
                        dmc.setSelectedDmcHasBusinessLicense(val?.has_business_license ?? null)
                      }}
                      menuPlacement="auto"
                    />
                  )}
                />
                <p>
                  <span
                    className="help"
                    data-testid="dmc-cant-find"
                    style={{ cursor: dmc.isCreatingTempDmc ? 'not-allowed' : 'pointer', color: dmc.isCreatingTempDmc ? '#aaa' : '#3273dc' }}
                    onClick={dmc.handleCantFindDmc}
                  >
                    {dmc.isCreatingTempDmc ? 'Loading Temporary DMC...' : "Can't find the DMC?"}
                  </span>
                </p>
              </div>
            )
          }

          if (m.column_name === 'is_active') {
            return (
              <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column py-2" key="is-active-dmc">
                <p className="has-text-grey is-small is-size-8">
                  <span className="mr-1">
                    Is Active <span className="has-text-danger">*</span>
                    <span
                      style={{ pointerEvents: 'auto' }}
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal`}
                      data-tooltip={'TODO:'}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={`pl-1 has-text-link`}
                      />
                    </span>
                  </span>
                </p>
                <label>
                  <Controller
                    name="is_active"
                    control={dmc.control}
                    defaultValue
                    render={({ field }) => (
                      <input
                        {...field}
                        className="checkbox is-small"
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked)
                          dmc.setIsActive(!!e.target.checked)
                        }}
                        disabled={dmc.isCreating}
                      />
                    )}
                  />
                </label>
              </div>
            )
          }

          if (m.column_name === 'dmc_status_dropdown_option_id') {
            // display:contents keeps this wrapper invisible to the Bulma
            // fields-container flex layout, which expects the FormFieldBuilder
            // column as a direct child. The wrapper exists only to carry the
            // data-testid for e2e selectors — do not replace with a Fragment.
            return (
              <div data-testid="dmc-status-field" key={m.id} style={{ display: 'contents' }}>
                <FormFieldBuilder
                  field={m}
                  selectOptions={selectOptions}
                  register={dmc.register}
                  errors={dmc.errors}
                  control={dmc.control}
                  row={dmc.moduleFields}
                  handleStateFields={(name, value) => {
                    dmc.handleStateFields(name, value, dmc.module)
                  }}
                  stateFields={dmc.stateFields}
                  sourceModule={sourceModule}
                  data={data}
                  fieldNotice={dmc.showConfirmedWarning ? (
                    <p
                      data-testid="dmc-status-confirmed-warning"
                      className="has-text-danger has-text-weight-bold mt-2"
                      style={{ whiteSpace: 'normal' }}
                    >
                      This will move the loan to Collections and can&apos;t be undone.<br />Are you sure?
                    </p>
                  ) : null}
                />
              </div>
            )
          }

          return (
            <FormFieldBuilder
              field={m}
              selectOptions={selectOptions}
              register={dmc.register}
              errors={dmc.errors}
              control={dmc.control}
              key={m.id}
              row={dmc.moduleFields}
              handleStateFields={(name, value) => {
                dmc.handleStateFields(name, value, dmc.module)
              }}
              stateFields={dmc.stateFields}
              sourceModule={sourceModule}
              data={data}
            />
          )
        });
      };

      let hasBusinessLicenseTag = <span className="is-size-7">—</span>
      if (dmc.selectedDmcOption) {
        hasBusinessLicenseTag = dmc.selectedDmcHasBusinessLicense
          ? <span className="tag is-success">Yes</span>
          : <span className="tag is-danger">No</span>
      }

      return (
        <>
          <div className="fields-container pl-1">
            Use this when the customer states they are using a Debt Management Company (DMC).
          </div>
          <div className="fields-container">
            {renderFields(filteredFields)}
            <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column py-2">
              <p className="has-text-grey is-small is-size-8">
                <span className="mr-1">Has Business License</span>
              </p>
              <p>{hasBusinessLicenseTag}</p>
            </div>
          </div>
          <div className="fields-container">
            <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column py-2">
              <p className="has-text-grey is-small is-size-8">
                <span className="mr-1">
                  Have Bank Details
                </span>
              </p>
              <label>
                <Controller
                  name="have_bank_details"
                  control={dmc.control}
                  defaultValue={false}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="checkbox is-small"
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked)
                        dmc.setHaveBankDetails(!!e.target.checked)
                      }}
                    />
                  )}
                />
              </label>
            </div>
          </div>
          {
            dmc.haveBankDetails && (
              <div className="fields-container">
                <div className="column is-full py-2">
                  <p className="subtitle is-4 has-text-weight-bold">Bank Details</p>
                </div>

                {dmc.loanDmcData?.ach_revocation_active ? (
                  <SpecialInstruction
                    content={'<span>' +
                      '<span>The customer has an ACH revocation, so ACH revocation will be removed if bank account information is added.</span> <br> ' +
                      " </span>"}
                    type="is-warning"
                  />
                ) : null}

                <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column py-2">
                  <p className="has-text-grey is-small is-size-8">
                    <span className="mr-1">Bank Account Holder Name</span><span className="has-text-danger">*</span>
                  </p>
                  <div>
                    <div style={{ width: '100%' }}>
                      <Controller
                        name="bank_account_holder_name"
                        control={dmc.control}
                        defaultValue={dmc.loanDmcData?.bank_account?.bank_account_holder_name ?? ''}
                        rules={{
                          required: true,
                          validate: {
                            specialCharacters: (name) => /^[a-zA-Z\s\-']+$/u.test(name) || "You must enter an account holder name with no special characters",
                          }
                        }}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="text"
                            className={`input is-small ${dmc.errors.bank_account_holder_name && 'is-danger'}`}
                            {...dmc.register('bank_account_holder_name')}
                          />
                        )}
                      />
                    </div>
                  </div>
                  <p className="is-hidden lox-capitalize-first-letter">Field error</p>
                </div>

                <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column py-2">
                  <p className="has-text-grey is-small is-size-8">
                    <span className="mr-1">Bank Routing Number</span><span className="has-text-danger">*</span>
                  </p>
                  <div>
                    <div style={{ width: '100%' }}>
                      <Controller
                        name="bank_routing_number_id"
                        control={dmc.control}
                        defaultValue={dmc.loanDmcData?.bank_account?.bank_routing_number?.number ? {
                          label: dmc.loanDmcData.bank_account.bank_routing_number.number,
                          value: dmc.loanDmcData.bank_account.bank_routing_number_id,
                        } : ''}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <AsyncSelect
                            {...field}
                            name="bank_routing_number_id"
                            {...dmc.register('bank_routing_number_id')}
                            classNames={reactSelectClassNames(dmc.errors, 'bank_routing_number_id')}
                            isSearchable
                            onChange={(val) => {
                              field.onChange({ label: val?.label, value: val?.id })
                              dmc.setBankName(val?.bank_label)
                            }}
                            cacheOptions
                            defaultOptions={false}
                            defaultValue={dmc.loanDmcData?.bank_account?.bank_routing_number?.number ? {
                              label: dmc.loanDmcData.bank_account.bank_routing_number.number,
                              value: dmc.loanDmcData.bank_account.bank_routing_number_id,
                            } : ''}
                            loadOptions={(inputValue) => dmc.getBankRoutingNumbers(inputValue, 'label', 'like')}
                            getOptionLabel={(e) => parseLabel(e)}
                            getOptionValue={(e) => parseValue(e)}
                            noOptionsMessage={({ inputValue }) => (!inputValue ? 'Start typing to search...' : 'No results found')}
                            styles={{
                              container: (base) => ({ ...base, width: '100%' }),
                              menu: ({ ...css }) => ({ ...css, width: 'max-content' }),
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                  <p>
                    <Link target="_blank" to="/bank_routing_number/form" className="help">
                      Can&apos;t find the Bank Routing Number?
                    </Link>
                  </p>
                </div>

                <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column py-2">
                  <p className="has-text-grey is-small is-size-8">
                    <span className="mr-1">Account Number</span><span className="has-text-danger">*</span>
                  </p>
                  <div>
                    <div style={{ width: '100%' }}>
                      <Controller
                        name="bank_account_number"
                        control={dmc.control}
                        rules={{ required: true }}
                        defaultValue={dmc.loanDmcData?.bank_account?.bank_account_number_last_four ? ('#####' + dmc.loanDmcData.bank_account.bank_account_number_last_four) : ''}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="text"
                            className={`input is-small ${dmc.errors.bank_account_number && 'is-danger'}`}
                            {...dmc.register('bank_account_number')}
                            onChange={(event) => {
                              dmc.handleBankAccountChange(event)
                              field.onChange(event)
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                  {dmc.loanDmcData?.bank_account?.bank_account_number_last_four && (
                    <div className="fields-container">
                      <div className="is-half-widescreen column" style={{ paddingTop: '0', paddingLeft: '0' }}>
                        <ViewSensitivePiiButtons
                          customerId={dmc.loanDmcData?.bank_account?.customer_id}
                          type={'full_bank_account_number'}
                          style={{ marginTop: '6px' }}
                          bankAccountId={dmc.loanDmcData?.bank_account?.id}
                          noMarginLeft
                          disabled={dmc.disableViewBAButton}
                        />
                      </div>
                      <div
                        className="is-half-widescreen column pl-0 pt-0 has-text-weight-bold"
                        style={{ marginTop: '10px' }}
                      >{dmc.fullBankAccountNumber}</div>
                    </div>
                  )}
                </div>

                <div className="is-one-quarter-widescreen is-one-third-desktop is-half-tablet column py-2">
                  <p className="has-text-grey is-small is-size-8">
                    <span className="mr-1">Bank Name</span>
                  </p>
                  <div>
                    <div style={{ width: '100%' }}>
                      <span>{dmc.bankName ? dmc.bankName : '-'}</span>
                    </div>
                  </div>
                </div>

              </div>
            )
          }
          <div className="fields-container">
            <div className="column is-full py-2">
              <p className="subtitle is-4 has-text-weight-bold">Supporting Files</p>
            </div>
          </div>
          <div className="fields-container">
            <div className="is-half-widescreen column py-2">
              <DmcDocumentSlot
                slotKey="dmc"
                label="Debt Management Company"
                docId={dmc.dmcDocId}
                docTypeId={dmc.dmcDocTypeId}
                file={dmc.dmcFile}
                onDrop={dmc.onDmcDrop}
                customerId={dmc.modal.options?.customerId}
                isActiveFieldName={null}
                viewPiiTooltipPosition="right"
                docColumnPaddingLeft={false}
              />
            </div>
          </div>
          <div className="fields-container">
            <div className="is-half-widescreen column py-2">
              <DmcDocumentSlot
                slotKey="poa"
                label="Power of Attorney"
                docId={dmc.poaDocId}
                docTypeId={dmc.poaDocTypeId}
                file={dmc.poaFile}
                onDrop={dmc.onPoaDrop}
                customerId={dmc.modal.options?.customerId}
                control={dmc.control}
                isActiveFieldName="poa_is_active"
                isActiveDisabled={dmc.isCreating || !dmc.isPoaActive}
                tooltipText="Set is_active for POA"
                tooltipPosition="top"
                viewPiiTooltipPosition="right"
                docColumnPaddingLeft={false}
              />
            </div>
            <div className="is-half-widescreen column py-2">
              <DmcDocumentSlot
                slotKey="tpc"
                label="TPC Communication Order"
                docId={dmc.tpcDocId}
                docTypeId={dmc.tpcDocTypeId}
                file={dmc.tpcFile}
                onDrop={dmc.onTpcDrop}
                customerId={dmc.modal.options?.customerId}
                isActiveFieldName={null}
                tooltipText="Documentation from TPC to stop contacting the customer. This could contain Cease & Desist language."
                tooltipPosition="left"
              />
            </div>
          </div>

          <div className="fields-container">
            <div className="is-half-widescreen column py-2">
              {dmc.showAchUploadOption || dmc.achDocId ? (
                <DmcDocumentSlot
                  slotKey="ach"
                  label="ACH Revocation"
                  docId={dmc.achDocId}
                  docTypeId={dmc.achDocTypeId}
                  file={dmc.achFile}
                  onDrop={dmc.onAchDrop}
                  customerId={dmc.modal.options?.customerId}
                  control={dmc.control}
                  isActiveFieldName="ach_revoke_is_active"
                  isActiveDisabled={dmc.isCreating || !dmc.achDocId}
                  tooltipText="Set is_active for ACH Revocation"
                  tooltipPosition="top"
                  viewPiiTooltipPosition="right"
                  docColumnPaddingLeft={false}
                />
              ) : (
                <div className="fields-container">
                  <div className="is-three-quarters-widescreen column py-2 pl-0">
                    <h1 className="title is-4">
                      <a
                        className="mr-1 has-text-black add-file-text"
                        data-testid="dmc-doc-ach-add"
                        onClick={() => dmc.setShowAchUploadOption(true)}
                      >
                        +Add ACH Revocation
                      </a>
                    </h1>
                  </div>
                </div>
              )}
            </div>
            <div className="is-half-widescreen column py-2">
              {dmc.showNoticeUploadOption || dmc.noticeDocId ? (
                <DmcDocumentSlot
                  slotKey="notice"
                  label="Drop Notice"
                  docId={dmc.noticeDocId}
                  docTypeId={dmc.noticeDocTypeId}
                  file={dmc.noticeFile}
                  onDrop={dmc.onNoticeDrop}
                  customerId={dmc.modal.options?.customerId}
                  control={dmc.control}
                  isActiveFieldName="drop_notice_is_active"
                  isActiveDisabled={dmc.isCreating || !dmc.noticeDocId}
                  tooltipText="Set is active for Drop Notice"
                  tooltipPosition="left"
                />
              ) : (
                <div className="fields-container">
                  <div className="is-three-quarters-widescreen column py-2 pl-0">
                    <h1 className="title is-4">
                      <a
                        className="mr-1 has-text-black add-file-text"
                        data-testid="dmc-doc-notice-add"
                        onClick={() => dmc.setShowNoticeUploadOption(true)}
                      >
                        +Add Drop Notice
                      </a>
                    </h1>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      );
    } else {
      return <Loading />
    }
  }

  // eslint-disable-next-line no-nested-ternary
  return dmc.modal.is_active && dmc.modal.type === 'dmc_modal' ? (
    // eslint-disable-next-line no-nested-ternary
    <form onSubmit={dmc.handleSubmit(dmc.onSubmit)} id="dmc_modal_form" className="lox-form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card modal-card-dmc column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Debt Management Company
            </p>
            <button className="delete" aria-label="close" onClick={dmc.handleCancel}></button>
          </header>
          <section
            className="modal-card-body"
            style={{ minHeight: '110px' }}
          >
            {!dmc.isLoaded && (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  <span className="has-text-center"><Loading /></span>
                </div>
              </div>
            )}

            {dmc.isLoaded && (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  {renderDebtManagementCompanyForm()}
                </div>
                <DmcActionSummary
                  isCreating={dmc.isCreating}
                  isActive={dmc.isActive}
                  showConfirmedWarning={dmc.showConfirmedWarning}
                />

              </div>
            )}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="button"
                className="button is-small has-background-grey has-text-white"
                onClick={dmc.handleCancel}
              >Cancel
              </button>
              <button type="submit" className="button is-link is-small" data-testid="dmc-modal-save" disabled={dmc.saving && 'disabled'}>{dmc.saving ? (
                <>
                  <Loading size="is-size-7" /> Saving</>
              ) : 'Save'}</button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) :
    null;
}
